import { computed, inject, Injectable } from "@angular/core";
import { SpecificationApiService } from "./specification-api.service";
import {
  SpecificationEtape,
  SpecificationEtapeMaster,
} from "../models/specification-etape.model";
import {
  Untrack,
  UpdateStatus,
} from "../../../shared/store/utils/store-utils.service";
import {
  EtatSpecification,
} from "../enums/etat-specif.enum";
import { ArticleInfos } from "../../articles/models/article.model";
import { EnumsUtils } from "../../../shared/utils/enums.utils";
import { ProcessEtape } from "../../process-etape/models/process_etape.model";
import { ProcessEtapeApiService } from "../../process-etape/services/process_etape-api.service";
import { Router } from "@angular/router";
import { catchError, firstValueFrom, map, race, Subject } from "rxjs";
import { ArticlesService } from "../../articles/services/articles.service";
import { SnackService } from "../../../shared/services/snack-service.service";
import { Nomenclature } from "../../nomenclature/models/nomenclature.model";
import { ArticlesInfosFacadeService } from "../../articles/services/article-infos/article-infos-facade.service";
import { EntityFacadeService } from "../../../shared/store/entity-store/entity-facade.service";
import {
  SpecificationState,
  SpecificationStoreService,
} from "./specification-store.service";
import { QueryParam } from "../../../shared/store/store-types.model";
import { ValidationSpecif } from "../../activity_infos/models/activity_infos.model";
import { SpecifModifs } from "../models/specif_modifs.model";
import { NomenclatureLigne } from "../../nomenclature/models/nomenclature_ligne.model";
import { SpecifImportPayload } from "../../articles/models/article-payload-duplicate.model";

@Injectable({ providedIn: "root" })
export class SpecificationFacadeService extends EntityFacadeService<
  SpecificationEtapeMaster,
  SpecificationApiService,
  SpecificationState
> {
  articleApi = inject(ArticlesService);
  router = inject(Router);
  processEtapeApi = inject(ProcessEtapeApiService);
  snackService = inject(SnackService);
  articleInfosFacade = inject(ArticlesInfosFacadeService);

  constructor() {
    const api = inject(SpecificationApiService);
    const store = inject(SpecificationStoreService);
    super(api, store);
    this.store.initStatus(SpecificationFacadeService);
  }

  initState() {
    this.store.patchState({
      sem: null,
      specifModif: [],
      selectedSpecificationEtapeId: null,
      articlePaths: null,
      editing: false,
    });
  }

  @Untrack
  @UpdateStatus
  async loadInStateMasterById(masterId: string) {
    const sem = await this.api.loadSEMByIdEagerWithParents(masterId);
    this.store.upsertOne(sem);
  }

  @Untrack
  @UpdateStatus
  async loadMasterById(masterId: string, specifId: string = null) {
    this.store.patchState({
      sem: null,
      specifModif: [],
      selectedSpecificationEtapeId: null,
      articlePaths: null,
    });
    const sem = await this.api.loadSEMByIdEagerWithParents(masterId);
    const specifModifs = await this.api.loadspecifModifs(masterId);

    this.store.getWritableState().sem.set(sem);
    this.store.getWritableState().specifModif.set(specifModifs);

    if (specifId) {
      if (this.store.getState().selectedSpecificationEtapeId() !== specifId) {
        this.store
          .getWritableState()
          .selectedSpecificationEtapeId.set(specifId);
      }
    } else {
      const etapeCourante = sem.versions.find(
        (v) => v.etat === EtatSpecification.EN_COURS,
      );
      if (etapeCourante) {
        this.store
          .getWritableState()
          .selectedSpecificationEtapeId.set(etapeCourante.id);
      } else {
        const maxVersion = sem.versions.reduce((max, obj) =>
          obj.version > max.version ? obj : max,
        );
        this.store
          .getWritableState()
          .selectedSpecificationEtapeId.set(maxVersion.id);
      }
    }
  }

  @Untrack
  @UpdateStatus
  async loadMasterByArticleId(articleId: string, specifId: string = null) {
    const article = await firstValueFrom(this.articleApi.loadById_(articleId));
    await this.loadMasterById(article.specificationEtapeMasterId, specifId);
  }

  @UpdateStatus
  async createMasterWithEtape(etape: SpecificationEtape) {
    const sem = await firstValueFrom(this.api.createMasterWithEtape(etape));
    this.store.upsertOne(sem);
    return sem;
  }

  @UpdateStatus
  async createNewSpeficicationEtape() {
    const se = await this.api.createNewSpeficicationEtape(
      this.store.getState().sem().id,
      this.store.getState().sem().articleId,
    );
    this.snackService.success("Étape de spécification crée");
    this.store.getWritableState().sem.update((sem) => {
      return {
        ...sem,
        versions: [...sem.versions, se],
      };
    });
    if (this.store.isStored(this.store.getState().sem().id)) {
      this.store.updateOne(this.store.getState().sem());
    }
    return se;
  }

  @UpdateStatus
  async updatePhoto(photoId: string) {
    const se = await this.api.updatePhoto(
      this.specificationEtapeSelected().infosArticleId,
      photoId,
    );
    this.snackService.success("Photo mise à jour");
    this.store.getWritableState().sem.update((sem) => {
      return {
        ...sem,
        versions: sem.versions.map((v) => (v.id === se.id ? se : v)),
      };
    });
    if (this.store.isStored(this.store.getState().sem().id)) {
      this.store.updateOne(this.store.getState().sem());
    }
  }

  @UpdateStatus
  async endRedaction(validationSpecif: ValidationSpecif) {
    const se = await this.api.endRedaction(
      this.specificationEtapeSelected().id,
    );
    this.snackService.success("Rédaction terminée");
    if (validationSpecif === ValidationSpecif.VALIDATION) {
      this.store.getWritableState().sem.update((sem) => {
        return {
          ...sem,
          versions: sem.versions.map((v) => (v.id === se.id ? se : v)),
        };
      });
    } else {
      this.store.getWritableState().sem.update((sem) => {
        const versions = sem.versions.map((v) => {
          if (v.etat === EtatSpecification.EN_COURS) {
            return { ...v, etat: EtatSpecification.ARCHIVED };
          } else if (v.id === se.id) {
            return se;
          }
          return v;
        });
        return {
          ...sem,
          versions,
        };
      });
    }
    if (this.store.isStored(this.store.getState().sem().id)) {
      this.store.updateOne(this.store.getState().sem());
    }
  }

  @UpdateStatus
  async validate() {
    const se = await this.api.validate(this.specificationEtapeSelected().id);
    this.snackService.success("Spécification validée");
    this.store.getWritableState().sem.update((sem) => {
      return {
        ...sem,
        versions: sem.versions.map((v) => (v.id === se.id ? se : v)),
      };
    });
    if (this.store.isStored(this.store.getState().sem().id)) {
      this.store.updateOne(this.store.getState().sem());
    }
  }

  @UpdateStatus
  async approuve() {
    const se = await this.api.approuve(this.specificationEtapeSelected().id);
    this.snackService.success("Spécification approuvée");
    this.store.getWritableState().sem.update((sem) => {
      const versions = sem.versions.map((v) => {
        if (v.etat === EtatSpecification.EN_COURS) {
          return { ...v, etat: EtatSpecification.ARCHIVED };
        } else if (v.id === se.id) {
          return se;
        }
        return v;
      });
      return {
        ...sem,
        versions,
      };
    });
    if (this.store.isStored(this.store.getState().sem().id)) {
      this.store.updateOne(this.store.getState().sem());
    }
  }

  @UpdateStatus
  async deleteProcessEtape(p: ProcessEtape) {
    const specifId = this.store.getState().selectedSpecificationEtapeId();
    await this.processEtapeApi.delete(p, specifId);
    this.snackService.success("Étape supprimée");
    const updated = {
      ...this.store.getState().sem(),
      versions: this.store
        .getState()
        .sem()
        .versions.map((v) => {
          if (v.id === specifId) {
            return {
              ...v,
              process: v.process.filter((pr) => pr.id !== p.id),
            };
          } else {
            return v;
          }
        }),
    };
    if (this.store.isStored(specifId)) {
      this.store.updateOne(updated);
    }
    this.store.patchState({
      sem: updated,
    });
    await this.reIndexProcessEtapes(
      this.store
        .getState()
        .sem()
        .versions.find((v) => v.id === specifId).process,
      false,
    );
  }

  @UpdateStatus
  async upsertProcessEtape(p: ProcessEtape) {
    if (p.id) {
      return this.updateProcessEtape(p);
    } else {
      return this.addProcessEtape(p);
    }
  }

  @UpdateStatus
  async addProcessEtape(p: ProcessEtape) {
    const specifId = this.store.getState().selectedSpecificationEtapeId();
    const etapeSaved = await this.processEtapeApi.save(p);
    this.snackService.success("Étape crée");
    const updated = {
      ...this.store.getState().sem(),
      versions: [
        ...this.store
          .getState()
          .sem()
          .versions.map((v) => {
            return v.id === specifId
              ? {
                  ...v,
                  process: [...v.process, etapeSaved],
                }
              : v;
          }),
      ],
    };
    if (this.store.isStored(specifId)) {
      this.store.updateOne(updated);
    }
    this.store.patchState({
      sem: updated,
    });
    return etapeSaved;
  }

  @UpdateStatus
  async updateProcessEtape(processEtape: ProcessEtape) {
    const specifId = this.store.getState().selectedSpecificationEtapeId();
    const etapeSaved = await this.processEtapeApi.save(processEtape);
    this.snackService.success("Étape mise à jour");
    const updated = {
      ...this.store.getState().sem(),
      versions: [
        ...this.store
          .getState()
          .sem()
          .versions.map((v) => {
            if (v.id === specifId) {
              return {
                ...v,
                process: [
                  ...v.process.map((p) =>
                    p.id === processEtape.id ? etapeSaved : p,
                  ),
                ],
              };
            } else {
              return v;
            }
          }),
      ],
    };
    if (this.store.isStored(specifId)) {
      this.store.updateOne(updated);
    }
    this.store.patchState({
      sem: updated,
    });
  }

  @UpdateStatus
  async dupplicateEtape(p: ProcessEtape) {
    const specifId = this.store.getState().selectedSpecificationEtapeId();
    const resSpecif = await this.processEtapeApi.duplicate(p);
    this.snackService.success("Étape dupliquée");
    this.store.patchState({
      sem: {
        ...this.store.getState().sem(),
        versions: [
          ...this.store
            .getState()
            .sem()
            .versions.map((v) => {
              return v.id === specifId
                ? {
                    ...resSpecif,
                  }
                : v;
            }),
        ],
      },
    });
    if (this.store.isStored(this.store.getState().sem().id)) {
      this.store.updateOne(this.store.getState().sem());
    }
  }

  @UpdateStatus
  async updateProcessEtapeDoc(processEtapeId: string, docId: string) {
    const specifId = this.store.getState().selectedSpecificationEtapeId();
    const etapeSaved = await this.processEtapeApi.uploadDoc(
      processEtapeId,
      docId,
    );
    this.snackService.success("Document mis à jour");
    const updated = {
      ...this.store.getState().sem(),
      versions: [
        ...this.store
          .getState()
          .sem()
          .versions.map((v) => {
            return v.id === specifId
              ? {
                  ...v,
                  process: v.process.map((p) => {
                    return p.id === processEtapeId ? etapeSaved : p;
                  }),
                }
              : v;
          }),
      ],
    };
    if (this.store.isStored(specifId)) {
      this.store.updateOne(updated);
    }
    this.store.patchState({
      sem: updated,
    });
  }

  @UpdateStatus
  async loadPathDoer(articleId: string, seId: string) {
    const etapePathDoer = await firstValueFrom(
      this.api.loadPathDoer(articleId, seId),
    );
    this.store.getWritableState().articlePaths.update((ap) => {
      return {
        ...ap,
        etapePathDoer,
      };
    });
  }

  @UpdateStatus
  async reIndexProcessEtapes(ps: ProcessEtape[], showToaster = true) {
    const specifId = this.store.getState().selectedSpecificationEtapeId();
    const specifSaved = await this.api.reindexProcessEtapes(
      this.refreshIndex(ps),
      specifId,
    );
    this.snackService.success("Étapes sauvegardées");
    const updated = {
      ...this.store.getState().sem(),
      versions: [
        ...this.store
          .getState()
          .sem()
          .versions.map((v) => {
            return v.id === specifId ? specifSaved : v;
          }),
      ],
    };
    if (this.store.isStored(specifId)) {
      this.store.updateOne(updated);
    }
    this.store.patchState({
      sem: updated,
    });
  }

  @UpdateStatus
  async updateArticleInfos(articleInfos: ArticleInfos) {
    const articleInfosSaved = await this.articleApi.saveInfos(articleInfos);
    const specifId = this.store.getState().selectedSpecificationEtapeId();
    const updated = {
      ...this.store.getState().sem(),
      versions: [
        ...this.store
          .getState()
          .sem()
          .versions.map((v) => {
            return v.id === specifId
              ? {
                  ...v,
                  infosArticle: articleInfosSaved,
                }
              : v;
          }),
      ],
    };
    if (this.store.isStored(specifId)) {
      this.store.updateOne(updated);
    }
    this.store.patchState({
      sem: updated,
    });
  }

  @UpdateStatus
  async updateNomenclature(nomenclature: Nomenclature) {
    const saved = await this.processEtapeApi.saveNomenclature(nomenclature);
    const specifId = this.store.getState().selectedSpecificationEtapeId();
    const updated = {
      ...this.store.getState().sem(),
      versions: [
        ...this.store
          .getState()
          .sem()
          .versions.map((v) => {
            return v.id === specifId
              ? {
                  ...v,
                  nomenclature: saved,
                }
              : v;
          }),
      ],
    };
    if (this.store.isStored(specifId)) {
      this.store.updateOne(updated);
    }
    this.store.patchState({
      sem: updated,
    });
  }

  // selectors

  public specificationEtapeSelected = computed(() => {
    const selectedId = this.store.getState().selectedSpecificationEtapeId();
    const sem = this.store.getState().sem();
    return sem?.versions.find((v) => v.id === selectedId);
  });

  public modelSem = computed(() => {
    if (this.specificationEtapeSelected()?.modelSpecificationEtapeMasterId) {
      return this.getById(
        this.specificationEtapeSelected().modelSpecificationEtapeMasterId,
      )?.();
    }
  });

  public modelSe = computed(() => {
    const model = this.modelSem();
    if (model) {
      return this.getVersion(
        model.versions,
        this.specificationEtapeSelected()?.dateFin,
      );
    }
  });

  specificationEtapeEnCours = computed(() => {
    return this.store
      .getState()
      .sem()
      ?.versions.filter((v) => v.isActive !== false)
      .find((v) => v.etat === EtatSpecification.EN_COURS);
  });
  specificationEtapeEnRedaction = computed(() => {
    return this.store
      .getState()
      .sem()
      ?.versions.filter((v) => v.isActive !== false)
      .find(
        (v) =>
          v.etat === EtatSpecification.EN_REDACTION ||
          v.etat === EtatSpecification.REDIGEE ||
          v.etat === EtatSpecification.VALIDEE ||
          v.etat === EtatSpecification.APPROUVEE,
      );
  });

  specificationEtapeAutresElements = computed(() => {
    const specifEnCoursId = this.specificationEtapeEnCours()?.id;
    const specifEnRedactionId = this.specificationEtapeEnRedaction()?.id;
    return this.store
      .getState()
      .sem()
      ?.versions.filter(
        (v) => v.isActive !== false && v.id !== specifEnCoursId && v.id !== specifEnRedactionId,
      )
      .map((v) => ({
        name: `${EnumsUtils.libelleFromEnum(EtatSpecification, 'enums.etat_specification', v.etat)} (${v.version_nb})`,
        value: v.id,
      }));
  });

  specificationEtapeAllElements = computed(() => {
    return this.store
      .getState()
      .sem()
      ?.versions.filter((v) => v.isActive !== false)
      .map((v) => ({
        name: `${EnumsUtils.libelleFromEnum(EtatSpecification, 'enums.etat_specification', v.etat)} (v${v.version_nb})`,
        value: v.id,
      }));
  });

  setSelectedSpecificationEtape(id: string) {
    this.store.getWritableState().selectedSpecificationEtapeId.set(id);
  }

  canEditSpecif = computed(() => {
    return (
      this.specificationEtapeSelected() &&
      this.specificationEtapeSelected().etat !== EtatSpecification.EN_COURS &&
      this.specificationEtapeSelected().etat !== EtatSpecification.ARCHIVED
    );
  });

  refreshIndex(processEtapes: ProcessEtape[]) {
    return processEtapes.map((a, index) => {
      return {
        ...a,
        uuid: Math.random().toString(),
        index: index + 1,
        controles: a.controles
          ? a.controles.map((ac, ic) => {
              return {
                ...ac,
                index: ic + 1,
              };
            })
          : a.controles,
      };
    });
  }

  async updateDocumentListeId(semId: string, documentListId: string) {
    await this.api.updateDocumentListeId(semId, documentListId);
    const sem = this.store.getState().sem;
    const updated = { ...sem(), documentListeId: documentListId };
    if (this.store.isStored(sem().id)) {
      this.store.updateOne(updated);
    }
    this.store.getWritableState().sem.set(updated);
  }

  @Untrack
  @UpdateStatus
  async loadAllFilter(filters: {
    page?: number;
    search?: string;
    etatSpecification?: EtatSpecification[];
    sousFamille?: string[];
    famille?: string[];
    others?: string[];
    modelSpecificationEtapeMasterId?: string;
    type?: 'ARTICLE' | 'SPECIFICATION';
  }) {
    const sems = await firstValueFrom(this.api.loadAllFilter(filters));
    this.store.addMany(sems);
    return sems;
  }

  @Untrack
  @UpdateStatus
  async loadAllModelsFilter(filters: {
    page?: number;
    search?: string;
    etats?: EtatSpecification[];
    archived?: boolean;
  }) {
    const sems = await firstValueFrom(this.api.loadAllModelsFilter(filters));
    this.store.addMany(sems);
    return sems;
  }

  @Untrack
  @UpdateStatus
  async loadByArticleId(id: string, query?: QueryParam) {
    const existingSignalEntity = this.store.getById(id);
    const canceler$ = new Subject<void>();
    const loadById$ = this.api.loadByArticleId(id, query).pipe(
      map((entity) => {
        return this.store.upsertOne(entity, {
          error: null,
          loading: false,
          canceler$: null,
        });
      }),
      catchError((e) => {
        if (existingSignalEntity()) {
          this.store.patchState({ error: e });
        }
        throw e;
      }),
    );
    const metaDatas = this.getMetadata(id);
    metaDatas()?.canceler$?.next();
    if (metaDatas()) {
      this.store.updateMetadata(id, {
        loading: true,
        error: null,
        canceler$: canceler$,
      });
    }
    return await firstValueFrom(race([canceler$, loadById$]));
  }

  async canDelete(id: string): Promise<boolean> {
    const result = await firstValueFrom(this.api.canDelete(id));
    return result.canDelete;
  }

  @Untrack
  @UpdateStatus
  async archive(id: string): Promise<SpecificationEtapeMaster> {
    const sem = await firstValueFrom(this.api.archive(id));
    this.store.upsertOne(sem);
    const currentSem = this.store.getState().sem();
    if (currentSem.id === id) {
      this.store.getWritableState().sem.set(sem);
    }
    return sem;
  }

  getByArticleId(articleId: string) {
    return this.elements().find((e) => e().articleId === articleId);
  }

  @Untrack
  @UpdateStatus
  async unArchive(id: string) {
    const sem = await firstValueFrom(this.api.unArchive(id));
    this.store.upsertOne(sem);
    const currentSem = this.store.getState().sem();
    if (currentSem.id === id) {
      this.store.getWritableState().sem.set(sem);
    }
  }

  @Untrack
  @UpdateStatus
  async removeMaster(id: string) {
    await firstValueFrom(this.api.remove(id));
    this.store.removeOne(id);
    await this.router.navigateByUrl('/articles', { replaceUrl: true });
  }

  @Untrack
  @UpdateStatus
  async removeSpecificationEtape(specificationEtapeId: string) {
    const sem: SpecificationEtapeMaster = await firstValueFrom(
      this.api.removeSpecificationEtape(specificationEtapeId),
    );

    if (sem) {
      this.store.getWritableState().sem.set({ ...sem });
      const maxVersion = sem.versions.reduce((max, obj) =>
        obj.version > max.version ? obj : max,
      );
      this.store
        .getWritableState()
        .selectedSpecificationEtapeId.set(maxVersion.id);
      if (!sem.articleId) {
        this.router.navigateByUrl(`/specification/master/${sem.id}`, {
          replaceUrl: true,
        });
      } else {
        this.router.navigateByUrl(`/specification/id/${sem.articleId}`, {
          replaceUrl: true,
        });
      }
    }
  }

  @Untrack
  @UpdateStatus
  async updateSEModel(
    specificationEtapeId: string,
    modelSpecificationEtapeId: string,
  ) {
    const sem = await firstValueFrom(
      this.api.updateSEModel(specificationEtapeId, modelSpecificationEtapeId),
    );
    this.store.upsertOne(sem);
    this.store.getWritableState().sem.set(sem);
    const currentSem = this.store.getState().sem();
    if (currentSem.id === sem.id) {
      this.store.getWritableState().sem.set(sem);
    }
    if (modelSpecificationEtapeId) {
      await this.loadInStateMasterById(modelSpecificationEtapeId);
    }
    return sem;
  }

  @Untrack
  @UpdateStatus
  async loadAllSpecifModel() {
    const sems = await firstValueFrom(this.api.loadSEMModels());
    this.store.addMany(sems);
  }

  @Untrack
  @UpdateStatus
  async loadAllSpecifModelForSelection() {
    const sems = await firstValueFrom(
      this.api.loadAllSpecifModelsForSelection(),
    );
    this.store.addMany(sems);
  }

  getSpecificationModels() {
    return computed(() => {
      return this.getAllEntities()()
        .filter((e) => !e.articleId)
    });
  }

  getSEMArticles() {
    return computed(() => {
      return this.getAllEntities()()
        .filter((e) => e.articleId)
    });
  }

  async loadAllModelLinked(id: string) {
    const sems = await firstValueFrom(this.api.loadSemModelLinked(id));
    this.store.addMany(sems);
  }

  @Untrack
  @UpdateStatus
  async updatePodIdForEtape(
    specificationEtapeId: string,
    podId: string,
    key: string,
  ) {
    await firstValueFrom(
      this.api.updatePodIdForEtape(specificationEtapeId, podId, key),
    );
    const sem = this.store.getState().sem();
    if (sem) {
      sem.versions.forEach((v) => {
        if (v.id === specificationEtapeId) {
          v[key] = podId;
        }
      });

      this.store.getWritableState().sem.set(sem);
    }
  }

  @UpdateStatus
  async saveSpecifModif(specifModifs: SpecifModifs) {
    const modif = await firstValueFrom(this.api.saveSpecifModifs(specifModifs));
    this.store.getWritableState().specifModif.update((sms) => {
      return [...sms.filter((sm) => sm.id !== modif.id), modif];
    });
  }

  @UpdateStatus
  async doneSpecifModif(specifModifs: SpecifModifs) {
    const modif = await firstValueFrom(this.api.doneSpecifModifs(specifModifs));
    this.store.getWritableState().specifModif.update((sms) => {
      return [...sms.filter((sm) => sm.id !== modif.id), modif];
    });
  }

  getVersion(versions: SpecificationEtape[], date: Date) {
    let matchingVersion;
    if (!date) {
      matchingVersion =
        versions.find((v) => v.etat === EtatSpecification.EN_COURS) || null;
    } else {
      matchingVersion = versions.find(
        (v) => new Date(v.dateDebut) <= date && new Date(v.dateFin) >= date,
      );
    }
    return (
      matchingVersion ||
      versions.find((v) => v.etat === EtatSpecification.EN_COURS) ||
      (versions.length === 1 ? versions[0] : null) ||
      null
    );
  }

  @Untrack
  @UpdateStatus
  async finishOcr(payload: {
    processes: ProcessEtape[];
    nomenclatureLignes: NomenclatureLigne[];
    denominationModel: string;
    denominationArticle: string;
  }) {
    const res: any = await firstValueFrom(this.api.finishOcr(payload));
    this.store.upsertOne(res);
    return res;
  }

  @Untrack
  @UpdateStatus
  async importFromArticle(payload: SpecifImportPayload) {
    const res: any = await firstValueFrom(this.api.importFromArticle(payload));
    this.store.getWritableState().sem.set(res);
    this.store.upsertOne(res);
    return res;
  }

  async update(entity: SpecificationEtapeMaster) {
    const sem = await super.update(entity);
    if (this.store.getState().sem()?.id === sem()?.id) {
      this.store.getWritableState().sem.update((oldSem) => {
        return {
          ...oldSem,
          ...sem(),
        };
      });
    }
    return sem;
  }

  @Untrack
  @UpdateStatus
  async updateSe(entity: SpecificationEtape) {
    const sem = await firstValueFrom(this.api.updateSe(entity));
    if (this.store.getState().sem()?.id === sem?.id) {
      this.store.getWritableState().sem.update((oldSem) => {
        return {
          ...oldSem,
          ...sem,
        };
      });
    }
    return sem;
  }
}
